// School & teacher details, typed once and reused on every printed report header,
// signature block and the school/teacher columns of the raw CSV. One row in the
// settings store; nothing here leaves the device.
import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, qs, onClick, toast } from '../dom.js';
import { icon } from '../icons.js';
import type { SettingsRecord } from '../../db/schema.js';

interface Field {
  name: keyof Omit<SettingsRecord, 'id' | 'updatedAt'>;
  label: string;
  placeholder?: string;
  hint?: string;
}

const GROUPS: Array<{ heading: string; fields: Field[] }> = [
  {
    heading: 'School',
    fields: [
      { name: 'schoolName', label: 'School name', placeholder: 'e.g. San Miguel National High School' },
      { name: 'schoolId', label: 'School ID', placeholder: 'e.g. 303456' },
      { name: 'division', label: 'Division', placeholder: 'e.g. Division of Bukidnon' },
      { name: 'district', label: 'District', placeholder: 'e.g. District II' },
    ],
  },
  {
    heading: 'Prepared by',
    fields: [
      { name: 'teacherName', label: 'Teacher name', placeholder: 'e.g. Juan A. Dela Cruz' },
      { name: 'teacherPosition', label: 'Position', placeholder: 'e.g. Teacher III' },
    ],
  },
  {
    heading: 'Noted by',
    fields: [
      { name: 'principalName', label: 'Principal name', placeholder: 'e.g. Maria B. Santos' },
      { name: 'principalPosition', label: 'Position', placeholder: 'e.g. School Principal II' },
    ],
  },
];

export async function settingsView(): Promise<RenderedView> {
  const settings = await repos.getSettings();

  const group = (heading: string, fields: Field[]) => `<p class="section-label">${esc(heading)}</p>
    <div class="card"><div class="stack">${fields
      .map(
        (f) => `<div class="field"><label for="st_${f.name}">${esc(f.label)}</label>
          <input class="input" id="st_${f.name}" value="${esc(settings[f.name])}" placeholder="${esc(f.placeholder ?? '')}" maxlength="120">
          ${f.hint ? `<p class="hint">${esc(f.hint)}</p>` : ''}</div>`,
      )
      .join('')}</div></div>`;

  return {
    title: 'School & teacher',
    subtitle: 'Used on printed reports',
    back: '/about',
    html: `<div class="view">
      <div class="card"><p style="margin:0" class="muted">These appear in the header and signature lines of every printed or exported report. Stored on this device only.</p></div>
      ${GROUPS.map((g) => group(g.heading, g.fields)).join('')}
      <p class="hint">Leave a field blank to print an empty line to fill in by hand.</p>
    </div>`,
    bottom: `<button class="btn btn-primary btn-block" data-save>${icon.check()} Save details</button>`,
    mount(root) {
      onClick(root, '[data-save]', async () => {
        const patch: Record<string, string> = {};
        for (const g of GROUPS) {
          for (const f of g.fields) {
            patch[f.name] = qs<HTMLInputElement>(root, `#st_${f.name}`)?.value.trim() ?? '';
          }
        }
        await repos.saveSettings(patch as Partial<SettingsRecord>);
        toast('Details saved');
        navigate('#/about');
      });
    },
  };
}
